import React, { useState } from "react";
import { Box, Button, Checkbox } from "@mui/material";
import TopTitleFormComponent from "./TopTitleFormComponent";
import { StandardDrawerFooter } from "./StandardDrawer";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { updateDoc, doc as firestoreDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { showCustomToast } from "../Misc/CustomToast";
import SignaturePadComponentV2 from "../Signature/SignaturePadComponentV2";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadString,
} from "firebase/storage";
import CircularProgress from "@mui/material/CircularProgress";
import "./AdmissionDocumentRedesign.css";

function AddClientOrientationChecklist(props) {
  const { clientId, closemodal } = props;
  const [signatureDataURL, setSignatureDataURL] = useState(null);
  const [isSignatureValidated, setIsSignatureValidated] = useState(false);
  const [, setIsSignatureStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSignatureSave = (dataURL) => {
    setSignatureDataURL(dataURL);
  };

  const uploadSignature = async () => {
    const storage = getStorage();
    const fileName = `${clientId}_orientation_signature.png`;
    const signatureRef = ref(
      storage,
      `MandatoryForms/ClientOrientationChecklist/${fileName}`
    );
    await uploadString(signatureRef, signatureDataURL, "data_url");
    const downloadURL = await getDownloadURL(signatureRef);
    return downloadURL;
  };

  // Définir les sections et items de la checklist
  const checklistSections = [
    {
      title: "Initial Introduction",
      items: [
        "Welcome the client and introduce them to the staff.",
        "Medical Office and Staff/ Support Office and Staff/ Clinical Operation Office and Staff/ F&B Staff/ Housekeeping",
      ],
    },
    {
      title: "Provide a tour of the facility",
      items: [
        "Common areas (lounges, dining hall, etc.)",
        "Facilitation rooms (First month & Second)",
        "Gym (On site & Outings)",
        "Casada areas",
      ],
    },
    {
      title: "Documentation and Paperwork in Bodhi",
      items: [
        "Complete intake forms (and Photo)",
        "Review and sign consent forms (and ROI)",
        "Provide a copy of the client handbook (in Room)",
        "Emergency Contact Form",
        "Review the code of conduct",
        "Explain the consequences of rule violations",
        "Emergency procedures and safety protocols",
        "Confidentiality policies",
      ],
    },
    {
      title: "Schedule Overview",
      items: [
        "Daily routine (e.g., wake-up time, Participation)",
        "Program sessions schedule",
        "Sunday outings details and timings",
        "Therapy sessions timing",
        "Introduction to the wellness program (e.g., yoga, meditation)",
      ],
    },
    {
      title: "Meal Times",
      items: [
        "Breakfast: 8:00 AM - 8:30 AM",
        "Lunch: 12:00 PM - 1:00 PM",
        "Dinner: 6:00 PM - 7:00 PM",
      ],
    },
    {
      title: "Program and Therapy Sessions",
      items: [
        "Individual therapy sessions",
        "Group therapy sessions",
        "One-on-one support sessions",
      ],
    },
    {
      title: "Workbook Material",
      items: [
        "Provide and explain the workbook materials",
        "Guide on how to use and complete the workbooks",
        "Schedule for workbook review sessions (1 on 1 Support session)",
      ],
    },
    {
      title: "Support Systems",
      items: [
        "Introduce the assigned therapist",
        "Meet the support staff",
        "Explanation of peer support groups",
      ],
    },
    {
      title: "General Information",
      items: [
        "Laundry schedule and facilities",
        "Visitor policies and on site rules",
        "Mail and communication guidelines",
      ],
    },
    {
      title: "Final Steps",
      items: [
        "Answer any questions the client may have",
        "Provide a contact list for staff members",
        "Schedule the first therapy session",
        "Ensure the client feels settled and supported",
      ],
    },
  ];

  // Initialiser l'état des réponses (clé = "sectionIndex_itemIndex")
  const initialResponses = {};
  checklistSections.forEach((section, sIndex) => {
    section.items.forEach((_, iIndex) => {
      initialResponses[`${sIndex}_${iIndex}`] = false;
    });
  });
  const [responses, setResponses] = useState(initialResponses);

  const handleToggle = (key) => {
    setResponses((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFormSubmit = async () => {
    // Vérifier que toutes les cases sont cochées
    const allChecked = Object.values(responses).every((val) => val === true);
    if (!allChecked) {
      showCustomToast(
        "Please complete the checklist by checking all items.",
        "error"
      );
      return;
    }

    if (!signatureDataURL || !isSignatureValidated) {
      showCustomToast("Please provide a valid signature.", "error");
      return;
    }
    setIsLoading(true);

    try {
      const signatureURL = await uploadSignature();
      // Enregistrer les réponses dans la collection "ClientOrientationChecklist"
      const checklistDocRef = doc(db, "ClientOrientationChecklist", clientId);
      await setDoc(checklistDocRef, {
        responses,
        signatureURL,
        signedOn: serverTimestamp(),
      });
      // Mettre à jour le document client dans "clients"
      const clientDocRef = firestoreDoc(db, "clients", clientId);
      await updateDoc(clientDocRef, {
        clientOrientationCheckListOk: true,
        clientOrientationCheckListDateSigned: serverTimestamp(),
      });
      showCustomToast("Checklist successfully submitted!", "success");
      setIsLoading(false);
      closemodal();
    } catch (error) {
      console.error("Error submitting checklist:", error);
      showCustomToast("Error submitting checklist", "error");
      setIsLoading(false);
    }
  };

  return (
    <div className="admission-document-shell orientation-form-shell">
      <TopTitleFormComponent
        title="Client Orientation Checklist"
        eyebrow="ADMISSION FORM"
        subtitle={`${props.firstName || "Client"} ${props.lastName || ""}`.trim()}
        closemodal={closemodal}
      />

      <div className="admission-document-body">
        {checklistSections.map((section, sIndex) => (
          <Box key={sIndex} className="admission-document-section" sx={{ marginBottom: "15px" }}>
            <h3>{section.title}</h3>
            {section.items.map((item, iIndex) => {
              const key = `${sIndex}_${iIndex}`;
              return (
                <Box
                  key={key}
                  className="admission-document-check-row"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "5px",
                  }}
                >
                  <Checkbox
                    checked={responses[key]}
                    onChange={() => handleToggle(key)}
                  />
                  <span>{item}</span>
                </Box>
              );
            })}
          </Box>
        ))}
        <div className="admission-document-signature">
          <SignaturePadComponentV2
            onSignatureSave={handleSignatureSave}
            signatureDataURL={signatureDataURL}
            onSignatureStart={() => setIsSignatureStarted(true)}
            onSignatureValidate={setIsSignatureValidated}
          />
        </div>
      </div>
      <StandardDrawerFooter className="admission-document-footer">
        <div style={{ gap: "20px" }}>
          <Button
            variant="contained"
            style={{
              backgroundColor: "white",
              color: "black",
              marginRight: "20px",
            }}
            // disabled={!(pic1Url && pic2Url && pic3Url && pic4Url && pic5Url)}
            onClick={props.closemodal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFormSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit Checklist"
            )}
          </Button>
        </div>
      </StandardDrawerFooter>
    </div>
  );
}

export default AddClientOrientationChecklist;
